import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import TicketDetailView from '../TicketDetailView';
import AssignTicketToAgentList from './componentes/AssignTicketToAgentList';
import AppContext from 'context/Context';
import {
    TicketsFilterTabs,
    TicketCardItem,
    TicketsEmptyState,
    TicketsLoadingState,
    AgentFilterSelect,
    EtiquetaFilterSelect,
    NivelFilterSelect,
    TicketsPagination
} from './componentes';
import { useTicketsList } from './hooks/useTicketsList';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';

const TicketsView = ({ onTicketSelect, selectedTicket, setSelectedTicket, onTicketDetailLoaded, onInitialReady }) => {
    const [internalSelectedTicket, setInternalSelectedTicket] = useState(null);
    const { config: { isDark } = {} } = useContext(AppContext);
    const [filterTab, setFilterTab] = useState('en_progreso');
    const [agents, setAgents] = useState([]);
    const [etiquetas, setEtiquetas] = useState([]);
    const [selectedEtiquetaId, setSelectedEtiquetaId] = useState('');
    const [selectedNivelId, setSelectedNivelId] = useState('');
    const [isLoadingEtiquetas, setIsLoadingEtiquetas] = useState(false);
    const { token, nombreCompleto, idUsuario } = useToken();
    const [selectedAgentId, setSelectedAgentId] = useState(idUsuario ? String(idUsuario) : '');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const hasStartedLoadingRef = useRef(false);
    const hasAutoCheckedTabRef = useRef(false);
    const hasInitializedAgentRef = useRef(false);
    const etiquetasLoadedRef = useRef(false);
    const { showSuccessToast, showErrorToast } = useToast();

    // Sincronizar selectedAgentId por defecto con el asesor logueado al iniciar
    useEffect(() => {
        if (idUsuario && !hasInitializedAgentRef.current) {
            hasInitializedAgentRef.current = true;
            setSelectedAgentId(String(idUsuario));
        }
    }, [idUsuario]);

    // Debounce de la búsqueda para optimizar peticiones
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Estados para el Modal de Asignación de Ticket
    const [ticketToAssign, setTicketToAssign] = useState(null);
    const [showAssignModal, setShowAssignModal] = useState(false);

    const currentTicket = selectedTicket !== undefined ? selectedTicket : internalSelectedTicket;
    const isViewingDetail = Boolean(currentTicket);

    // Solo consultar la lista de tickets a la BD si NO estamos dentro del detalle de un ticket
    const {
        tickets,
        counts,
        isLoading,
        page,
        setPage,
        pageSize,
        setPageSize,
        totalRegistros,
        totalPages,
        reloadTickets,
        markTicketAsRead
    } = useTicketsList(
        filterTab,
        selectedAgentId,
        !isViewingDetail,
        20,
        onInitialReady,
        null,
        null,
        debouncedSearchTerm,
        selectedEtiquetaId,
        selectedNivelId
    );

    // Si no hay tickets en progreso al terminar la primera carga, cambiar a pendientes por defecto
    useEffect(() => {
        if (isLoading) {
            hasStartedLoadingRef.current = true;
        }
        if (hasStartedLoadingRef.current && !isLoading && !hasAutoCheckedTabRef.current) {
            if (counts && counts.enProgreso === 0) {
                setFilterTab('pendientes');
            }
            hasAutoCheckedTabRef.current = true;
        }
    }, [isLoading, counts]);

    // Cargar catálogo de incidencias/etiquetas activas
    const fetchEtiquetas = useCallback(async () => {
        if (!token || etiquetasLoadedRef.current) return;
        setIsLoadingEtiquetas(true);
        try {
            const resp = await fetch('/api/SupportEngine/GetEtiquetas', {
                headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
            });
            if (resp.ok) {
                const result = await resp.json();
                const data = result.data || (Array.isArray(result) ? result : []);
                setEtiquetas(Array.isArray(data) ? data : []);
                etiquetasLoadedRef.current = true;
            }
        } catch {
            // Silencioso
        } finally {
            setIsLoadingEtiquetas(false);
        }
    }, [token]);

    useEffect(() => {
        fetchEtiquetas();
    }, [fetchEtiquetas]);

    const [isLoadingAgents, setIsLoadingAgents] = useState(false);
    const isFetchingAgentsRef = useRef(false);
    const agentsLoadedRef = useRef(false);

    // Cargar lista de agentes activos
    const fetchAgents = useCallback(async () => {
        if (!token || isFetchingAgentsRef.current || agentsLoadedRef.current) return;
        isFetchingAgentsRef.current = true;
        setIsLoadingAgents(true);
        try {
            const response = await fetch('/api/SupportEngine/GetAgentsList', {
                headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
            });
            if (response.ok) {
                const result = await response.json();
                const data = result.data || (Array.isArray(result) ? result : []);
                setAgents(Array.isArray(data) ? data : []);
                agentsLoadedRef.current = true;
            }
        } catch {
            // Fallback silencioso
        } finally {
            isFetchingAgentsRef.current = false;
            setIsLoadingAgents(false);
        }
    }, [token]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    // 1. Abre el modal de asignación y asegura que la lista de asesores esté cargada
    const handleOpenAssignModal = (ticket) => {
        if (agents.length === 0) {
            fetchAgents();
        }
        setTicketToAssign(ticket);
        setShowAssignModal(true);
    };

    // 2. Ejecuta la asignación cuando el usuario confirma en el modal
    const handleConfirmAssignTicket = async (ticket, selectedAgent) => {
        try {
            const targetAgent = selectedAgent || {
                idUsuario: Number(idUsuario),
                vNombreCompleto: nombreCompleto || 'Asesor'
            };

            const payload = {
                idTicket: ticket.idTicket,
                idUsuarioAsignado: Number(targetAgent.idUsuario),
                vUpdatedUser: targetAgent.vNombreCompleto || nombreCompleto || 'Asesor'
            };

            const response = await fetch('/api/SupportEngine/AssignTicketToAgent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + token
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || 'Error al asignar el ticket');
            }

            if (showSuccessToast) {
                showSuccessToast(
                    'Asignación exitosa',
                    `Ticket #${ticket.idTicket} asignado a ${targetAgent.vNombreCompleto}. Saludo de bienvenida enviado.`
                );
            }

            reloadTickets();
        } catch (err) {
            if (showErrorToast) {
                showErrorToast('Error de asignación', err.message);
            }
        }
    };

    const handleSelectTicket = ticket => {
        if (!ticket) return;
        const ticketId = ticket.idTicket || ticket.id;
        if (markTicketAsRead) {
            markTicketAsRead(ticketId);
        }
        window.open(`/support-engine/ticket/${ticketId}`, '_blank');
    };

    const handleBackToList = () => {
        const currentTicketId = currentTicket?.idTicket || currentTicket?.id;
        if (currentTicketId && markTicketAsRead) {
            markTicketAsRead(currentTicketId);
        }
        if (setSelectedTicket) {
            setSelectedTicket(null);
        } else {
            setInternalSelectedTicket(null);
        }
        if (onTicketSelect) {
            onTicketSelect(null);
        }
    };

    if (currentTicket) {
        return (
            <TicketDetailView
                ticket={currentTicket}
                onBack={handleBackToList}
                onTicketUpdated={reloadTickets}
                onTicketDetailLoaded={(data) => {
                    if (onTicketDetailLoaded) onTicketDetailLoaded(data);
                    if (onInitialReady) onInitialReady();
                }}
                onSelectTicket={handleSelectTicket}
            />
        );
    }

    return (
        <div className="tickets-view-container">
            {/* Top Filter Tabs Bar with Agent Filter */}
            <div
                className="rounded-3 p-2 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2 shadow-none"
                style={{
                    backgroundColor: isDark ? '#121e2d' : '#ffffff',
                    border: isDark ? '1px solid #233142' : '1px solid #e2e8f0'
                }}
            >
                <TicketsFilterTabs
                    filterTab={filterTab}
                    setFilterTab={setFilterTab}
                    counts={counts}
                />
                <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
                    {/* Buscador por celular o ticket */}
                    <div className="position-relative" style={{ minWidth: '250px', maxWidth: '270px' }}>
                        <input
                            type="text"
                            className="form-control form-control-sm ps-4 pe-4"
                            placeholder="Buscar por celular o # ticket..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            style={{
                                backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                                borderColor: isDark ? '#233142' : '#e2e8f0',
                                color: isDark ? '#f8fafc' : '#334155',
                                height: '34px',
                                fontSize: '0.82rem',
                                borderRadius: '6px'
                            }}
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            className="position-absolute text-muted"
                            style={{
                                left: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: '0.75rem',
                                pointerEvents: 'none'
                            }}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                className="btn btn-link position-absolute p-0 text-muted border-0 shadow-none"
                                style={{
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    fontSize: '0.75rem'
                                }}
                                title="Limpiar búsqueda"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>

                    <AgentFilterSelect
                        selectedAgentId={selectedAgentId}
                        onSelectAgent={setSelectedAgentId}
                        agents={agents}
                        currentUserId={idUsuario}
                        currentUserName={nombreCompleto}
                        isLoadingAgents={isLoadingAgents}
                    />

                    {/* Filtro por incidencia (etiqueta) */}
                    <EtiquetaFilterSelect
                        selectedEtiquetaId={selectedEtiquetaId}
                        onSelectEtiqueta={setSelectedEtiquetaId}
                        etiquetas={etiquetas}
                        isLoading={isLoadingEtiquetas}
                    />

                    {/* Filtro por nivel de soporte */}
                    <NivelFilterSelect
                        selectedNivelId={selectedNivelId}
                        onSelectNivel={setSelectedNivelId}
                    />
                </div>
            </div>

            {/* Ticket List Cards / Loader / Empty State / Pagination */}
            {isLoading && tickets.length === 0 ? (
                <TicketsLoadingState />
            ) : tickets.length === 0 ? (
                <TicketsEmptyState filterTab={filterTab} hasSearch={Boolean(debouncedSearchTerm)} onReload={reloadTickets} />
            ) : (
                <>
                    <div className="d-flex flex-column gap-3">
                        {tickets.map(ticket => (
                            <TicketCardItem
                                key={ticket.idTicket}
                                ticket={ticket}
                                agents={agents}
                                isLoadingAgents={isLoadingAgents}
                                onRequestAgents={fetchAgents}
                                onSelectTicket={handleSelectTicket}
                                onAssignTicketAgent={handleOpenAssignModal} // <-- Abre el modal al hacer clic en "Asignar Ticket"
                            />
                        ))}
                    </div>

                    <TicketsPagination
                        currentPage={page}
                        totalPages={totalPages}
                        totalRecords={totalRegistros}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={size => {
                            setPageSize(size);
                            setPage(1);
                        }}
                        isLoading={isLoading}
                    />
                </>
            )}

            {/* MODAL DE ASIGNACIÓN DE ASESOR */}
            <AssignTicketToAgentList
                show={showAssignModal}
                onHide={() => setShowAssignModal(false)}
                ticket={ticketToAssign}
                agents={agents}
                currentUserId={idUsuario}
                currentUserName={nombreCompleto}
                onConfirm={handleConfirmAssignTicket}
            />
        </div>
    );
};

export default TicketsView;
